import { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import {
  color,
  Field2 as Field,
  VibeIcon,
  viTrash,
} from 'vibeguide';
import {
  isEmpty,
} from 'lodash';
import './CreativeDateTime.scss';

const hourOptions = [];
for (let i = 0; i <= 24; i++) {
  const hour = i < 10 ? `0${i}:00` : `${i}:00`;
  hourOptions.push({
    label: hour,
    value: hour,
  });
}
function CreativeDateTime({
  startDate = '',
  endDate = '',
  segmentStartDate = '',
  segmentEndDate = '',
  dateRangeIndex = 0,
  onStartDateUpdate = () => {},
  onEndDateUpdate = () => {},
  mediaDateErrors = [],
  clickDeleteCreativeDates = () => {},
}) {
  const [errorState, setErrorState] = useState(false);

  const disableDayPartDates = isEmpty(segmentStartDate) || isEmpty(segmentEndDate);

  const minDate = moment(segmentStartDate).toDate();
  const maxDate = moment(segmentEndDate).toDate();

  const checkForMediaDateError = () => {
    if (mediaDateErrors.filter(err => err.index === dateRangeIndex)[0]) {
      return mediaDateErrors.filter(err => err.index === dateRangeIndex)[0].error;
    }
    return false;
  };

  const selectStartDateUpdate = (creativeStartDate) => {
    onStartDateUpdate({
      startDate: creativeStartDate,
      dateRangeIndex,
    });
  };

  const selectEndDateUpdate = (creativeEndDate) => {
    onEndDateUpdate({
      endDate: creativeEndDate,
      dateRangeIndex,
    });
  };

  useEffect(() => {
    if (!isEmpty(mediaDateErrors)) {
      setErrorState(checkForMediaDateError());
    }
  }, [mediaDateErrors]);

  return (
    <div className={classNames('CreativeDateTime', { 'error-state': errorState })}>
      {dateRangeIndex > 0 && (
        <div className="creative-dates-delete">
          <VibeIcon
            icon={viTrash}
            color={color.primary}
            hoverColor={color.primary60}
            size={16}
            tooltip="Delete Creative Dates"
            data-testid={`creative-date-time-${dateRangeIndex}-delete-button`}
            onClick={() => clickDeleteCreativeDates(dateRangeIndex)}
          />
        </div>
      )}

      <Grid spacing={2} container>
        <Grid size={6}>
          <Field
            type="datepicker"
            label="Start Date"
            placeholder="YYYY-MM-DD"
            value={startDate}
            dateProps={{
              format: 'YYYY-MM-DD',
              minDate,
              maxDate,
            }}
            tabIndex={36}
            testId={`creative-date-time-${dateRangeIndex}-start-date-input`}
            onChange={selectStartDateUpdate}
            disabled={disableDayPartDates}
          />
        </Grid>

        <Grid size={6}>
          <Field
            type="datepicker"
            label="End Date"
            placeholder="YYYY-MM-DD"
            value={endDate}
            dateProps={{
              format: 'YYYY-MM-DD',
              minDate: moment(startDate).toDate(),
              maxDate,
            }}
            tabIndex={37}
            testId={`creative-date-time-${dateRangeIndex}-end-date-input`}
            onChange={selectEndDateUpdate}
            disabled={disableDayPartDates || isEmpty(startDate)}
          />
        </Grid>

        {errorState && (
          <Grid size={12}>
            <div className="dates-error-message">Dates cannot overlap and must be continuous</div>
          </Grid>
        )}
      </Grid>
    </div>
  );
}

CreativeDateTime.propTypes = {
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  segmentStartDate: PropTypes.string,
  segmentEndDate: PropTypes.string,
  dateRangeIndex: PropTypes.number,
  onStartDateUpdate: PropTypes.func,
  onEndDateUpdate: PropTypes.func,
  mediaDateErrors: PropTypes.arrayOf(PropTypes.shape({
    index: PropTypes.number,
    error: PropTypes.bool,
  })),
};

export default CreativeDateTime;
